import React, { FormEvent, useMemo, useState } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { createApprovalTemplate, deleteApprovalTemplate, updateApprovalTemplate } from '../../services/mutations';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import Badge from '../../components/ui/Badge';
import {
  Button,
  DocumentTextIcon,
  Label,
  ListEmptyState,
  NumberInput,
  Select,
  Switch,
  TextInput,
  useConfirm,
} from '../../components/ui';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  InfoIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SpinnerIcon,
  TrashIcon,
  UserPlusIcon,
  XIcon,
} from '../../components/ui/Icons';
import { isActiveLookupItem } from '../../lib/isActiveLookup';
import { ApprovalOnApproveAction, ApprovalOnRejectAction, ApprovalStageMode, ApprovalTemplate, useApprovalTemplates, useApprovalTypes } from './useData';
import { useDocumentFileTypes } from '../documents/useData';

type User = { id: number; userDisplayName?: string; userFirstName: string; userLastName: string };
type Department = { id: number; name?: string; departmentName?: string };
type CrewRank = { id: number; code?: string | null; name: string; abrv?: string | null };
type StepDraft = {
  stage: number;
  sequence: number;
  label: string;
  stageMode: ApprovalStageMode;
  onApprove: ApprovalOnApproveAction;
  onReject: ApprovalOnRejectAction;
  assigneeUserId?: number | null;
  assigneeDepartmentId?: number | null;
  assigneeRankId?: number | null;
  assignMode?: 'user' | 'department' | 'rank';
  /** Hours after stage unlock before this step is considered overdue. */
  slaHours?: number | null;
  escalateUserId?: number | null;
  escalateDepartmentId?: number | null;
};

const STEP_TYPE_OPTIONS = ['Review', 'Approval', 'Review & Approval'] as const;

function defaultsForType(label: string, isLastStage: boolean): Pick<StepDraft, 'onApprove' | 'onReject'> {
  return {
    onApprove: isLastStage ? 'COMPLETE' : 'CONTINUE',
    onReject: label === 'Review' ? 'RETURN' : 'REJECT',
  };
}

const empty = (): Omit<ApprovalTemplate, 'id' | 'steps'> & { steps: StepDraft[] } => ({
  code: '',
  name: '',
  approvalTypeId: 0,
  contextKey: null,
  isActive: true,
  steps: [{ stage: 1, sequence: 0, label: 'Review', assignMode: 'user', stageMode: 'ALL', ...defaultsForType('Review', true) }],
});

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  return normalizePaginatedListResponse<T>(data as any, 1, 500).data;
}

function userLabel(user: User) {
  return user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim();
}

function rankLabel(rank: CrewRank) {
  return rank.abrv ? `${rank.name} (${rank.abrv})` : rank.name;
}

function formatAmountBand(min?: number | string | null, max?: number | string | null) {
  const minNum = min != null && min !== '' ? Number(min) : null;
  const maxNum = max != null && max !== '' ? Number(max) : null;
  if (minNum != null && maxNum != null) return `${minNum.toLocaleString()}–${maxNum.toLocaleString()}`;
  if (minNum != null) return `≥ ${minNum.toLocaleString()}`;
  if (maxNum != null) return `≤ ${maxNum.toLocaleString()}`;
  return '';
}

function groupStepsByStage(steps: Array<{ stage: number }>) {
  const order: number[] = [];
  const map = new Map<number, number[]>();
  steps.forEach((step, index) => {
    if (!map.has(step.stage)) {
      map.set(step.stage, []);
      order.push(step.stage);
    }
    map.get(step.stage)!.push(index);
  });
  return order.map((stage) => ({ stage, indices: map.get(stage)! }));
}

const FlowArrowDown: React.FC<{ label?: string }> = ({ label }) => (
  <div className="relative flex flex-col items-center py-2" aria-hidden>
    {label ? (
      <div className="flex flex-col items-center">
        <svg width="14" height="10" viewBox="0 0 14 10" className="text-slate-400 dark:text-slate-500">
          <line x1="7" y1="0" x2="7" y2="10" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500 shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
          {label}
        </span>
        <svg width="14" height="14" viewBox="0 0 14 14" className="text-slate-400 dark:text-slate-500">
          <line x1="7" y1="0" x2="7" y2="8" stroke="currentColor" strokeWidth="1.5" />
          <path d="M3.5 6 L7 11 L10.5 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    ) : (
      <svg width="14" height="22" viewBox="0 0 14 22" className="text-slate-400 dark:text-slate-500">
        <line x1="7" y1="0" x2="7" y2="14" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3.5 12 L7 17 L10.5 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
);

const FlowFork: React.FC<{ count: number }> = ({ count }) => {
  if (count < 2) return null;
  // Two side-by-side cards
  if (count === 2) {
    return (
      <div className="relative mx-auto mb-1 h-6 w-full max-w-[220px]" aria-hidden>
        <svg viewBox="0 0 220 24" className="h-full w-full text-slate-400 dark:text-slate-500">
          <line x1="110" y1="0" x2="110" y2="8" stroke="currentColor" strokeWidth="1.5" />
          <line x1="40" y1="8" x2="180" y2="8" stroke="currentColor" strokeWidth="1.5" />
          <line x1="40" y1="8" x2="40" y2="16" stroke="currentColor" strokeWidth="1.5" />
          <line x1="180" y1="8" x2="180" y2="16" stroke="currentColor" strokeWidth="1.5" />
          <path d="M36 14 L40 20 L44 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M176 14 L180 20 L184 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    );
  }
  // 3+ stacked: single down arrow into the list
  return (
    <div className="flex justify-center" aria-hidden>
      <svg width="14" height="16" viewBox="0 0 14 16" className="text-slate-400 dark:text-slate-500">
        <line x1="7" y1="0" x2="7" y2="8" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3.5 6 L7 11 L10.5 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const FlowJoin: React.FC = () => (
  <div className="relative mx-auto mt-1 h-6 w-full max-w-[220px]" aria-hidden>
    <svg viewBox="0 0 220 24" className="h-full w-full text-slate-400 dark:text-slate-500">
      <line x1="40" y1="2" x2="40" y2="10" stroke="currentColor" strokeWidth="1.5" />
      <line x1="180" y1="2" x2="180" y2="10" stroke="currentColor" strokeWidth="1.5" />
      <line x1="40" y1="10" x2="180" y2="10" stroke="currentColor" strokeWidth="1.5" />
      <line x1="110" y1="10" x2="110" y2="16" stroke="currentColor" strokeWidth="1.5" />
      <path d="M106 14 L110 20 L114 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </div>
);

const WorkflowFlowchart: React.FC<{
  name: string;
  steps: StepDraft[];
  users: User[];
  departments: Department[];
  ranks: CrewRank[];
  colors: ReturnType<typeof useTheme>['colors'];
}> = ({ name, steps, users, departments, ranks, colors }) => {
  const groups = groupStepsByStage(steps);

  const assigneeName = (step: StepDraft) => {
    if (step.assigneeUserId) {
      const user = users.find((u) => u.id === step.assigneeUserId);
      return user ? userLabel(user) : `User #${step.assigneeUserId}`;
    }
    if (step.assigneeDepartmentId) {
      const dept = departments.find((d) => d.id === step.assigneeDepartmentId);
      return dept?.name || dept?.departmentName || `Dept #${step.assigneeDepartmentId}`;
    }
    if (step.assigneeRankId) {
      const rank = ranks.find((r) => r.id === step.assigneeRankId);
      return rank ? `${rankLabel(rank)} (rank)` : `Rank #${step.assigneeRankId}`;
    }
    return 'Unassigned';
  };

  return (
    <div className={cn('overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
      <div className={cn('border-b px-4 py-3', colors.border)}>
        <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>Flow preview</p>
        <p className={cn('mt-0.5 truncate text-sm font-semibold', colors.text)}>{name.trim() || 'Untitled template'}</p>
      </div>
      <div className="px-4 py-5">
        {groups.length === 0 ? (
          <p className={cn('py-6 text-center text-sm', colors.textMuted)}>Add stages to preview the flow</p>
        ) : (
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide shadow-sm',
                'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800',
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Start
            </div>

            {groups.map((group, groupIndex) => {
              const isParallel = group.indices.length > 1;
              const stageMode = steps[group.indices[0]]?.stageMode === 'ANY' ? 'ANY' : 'ALL';
              const sideBySide = isParallel && group.indices.length === 2;
              return (
                <div key={group.stage} className="flex w-full flex-col items-center">
                  <FlowArrowDown label={groupIndex === 0 ? undefined : 'Then'} />

                  <div className="w-full">
                    <div className="mb-2 flex items-center justify-center gap-2">
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold text-white">
                        {groupIndex + 1}
                      </span>
                      <span className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                        Stage {groupIndex + 1}
                      </span>
                    </div>

                    {isParallel && (
                      <div className="mb-1.5 flex justify-center">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                            stageMode === 'ANY'
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                              : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
                          )}
                        >
                          {stageMode === 'ANY' ? 'Any one can approve' : 'All must approve'}
                        </span>
                      </div>
                    )}

                    <FlowFork count={group.indices.length} />

                    <div className={cn('gap-2', sideBySide ? 'grid grid-cols-2' : 'space-y-2')}>
                      {group.indices.map((index, stepInStage) => {
                        const step = steps[index];
                        const assigned = Boolean(step.assigneeUserId || step.assigneeDepartmentId);
                        return (
                          <div
                            key={index}
                            className={cn(
                              'rounded-xl border px-3 py-2.5 text-center shadow-sm',
                              colors.border,
                              colors.surface,
                            )}
                          >
                            {isParallel && (
                              <p className={cn('mb-1 text-[9px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                                {stageMode === 'ANY' ? `Option ${stepInStage + 1}` : `Approver ${stepInStage + 1}`}
                              </p>
                            )}
                            <p className={cn('text-xs font-semibold', colors.text)}>{step.label || 'Step'}</p>
                            <p
                              className={cn(
                                'mt-0.5 truncate text-[11px]',
                                assigned ? colors.textMuted : 'text-amber-600 dark:text-amber-400',
                              )}
                            >
                              {assigneeName(step)}
                            </p>
                            <div className="mt-2 flex flex-wrap justify-center gap-1">
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                <svg width="8" height="8" viewBox="0 0 8 8" className="opacity-80" aria-hidden>
                                  <path d="M1 4h4.5M3.5 1.5 6 4 3.5 6.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                                {step.onApprove === 'COMPLETE' ? 'Approved' : 'Next'}
                              </span>
                              <span
                                className={cn(
                                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                                  step.onReject === 'RETURN'
                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                    : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
                                )}
                              >
                                <svg width="8" height="8" viewBox="0 0 8 8" className="opacity-80" aria-hidden>
                                  <path d="M7 4H2.5M4.5 1.5 2 4l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                                {step.onReject === 'RETURN' ? 'Draft' : 'Rejected'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {sideBySide && stageMode === 'ALL' && <FlowJoin />}
                  </div>
                </div>
              );
            })}

            <FlowArrowDown />

            <div className="w-full">
              <div
                className={cn(
                  'mx-auto w-fit rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide',
                  colors.border,
                  colors.text,
                )}
              >
                Outcomes
              </div>

              {/* Colored branch into Approved / Rejected */}
              <div className="relative mx-auto mt-1 h-9 w-full max-w-[240px]" aria-hidden>
                <svg viewBox="0 0 240 36" className="h-full w-full">
                  <line x1="120" y1="0" x2="120" y2="10" stroke="currentColor" strokeWidth="1.5" className="text-slate-400 dark:text-slate-500" />
                  <line x1="50" y1="10" x2="190" y2="10" stroke="currentColor" strokeWidth="1.5" className="text-slate-400 dark:text-slate-500" />
                  {/* Approve branch */}
                  <line x1="50" y1="10" x2="50" y2="24" stroke="#10b981" strokeWidth="1.5" />
                  <path d="M46 20 L50 28 L54 20" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" />
                  {/* Reject branch */}
                  <line x1="190" y1="10" x2="190" y2="24" stroke="#ef4444" strokeWidth="1.5" />
                  <path d="M186 20 L190 28 L194 20" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-center shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/30">
                  <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">Approved</p>
                  <p className="mt-0.5 text-[10px] leading-snug text-emerald-700/80 dark:text-emerald-400/80">
                    Stages complete
                  </p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 px-2.5 py-2 text-center shadow-sm dark:border-red-900/50 dark:bg-red-950/30">
                  <p className="text-[11px] font-semibold text-red-700 dark:text-red-300">Rejected</p>
                  <p className="mt-0.5 text-[10px] leading-snug text-red-700/80 dark:text-red-400/80">
                    Final reject
                  </p>
                </div>
              </div>

              <div className="relative mx-auto mt-2 h-6 w-full max-w-[160px]" aria-hidden>
                <svg viewBox="0 0 160 24" className="h-full w-full text-amber-500/70 dark:text-amber-400/60">
                  <line x1="80" y1="0" x2="80" y2="12" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
                  <path d="M76 8 L80 16 L84 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              <div className="space-y-2">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-center dark:border-amber-900/50 dark:bg-amber-950/30">
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Returned to Draft</p>
                  <p className="mt-0.5 text-[10px] leading-snug text-amber-700/80 dark:text-amber-400/80">
                    When reject action is “return”
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-center dark:border-slate-700 dark:bg-slate-800/50">
                  <p className={cn('text-[11px] font-semibold', colors.text)}>Cancelled</p>
                  <p className={cn('mt-0.5 text-[10px] leading-snug', colors.textMuted)}>
                    Withdrawn anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const Templates: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const confirm = useConfirm();
  const { data: templates = [], mutate, isLoading } = useApprovalTemplates();
  const { data: typesRaw = [] } = useApprovalTypes();
  const { data: fileTypesRaw = [] } = useDocumentFileTypes();
  const { data: usersRaw } = useSWR('/users?limit=500', fetcher);
  const { data: departmentsRaw } = useSWR('/departments?limit=500', fetcher);
  const { data: ranksRaw } = useSWR('/crew-lookups/ranks?page=1&limit=500', fetcher);
  const types = useMemo(() => typesRaw.filter(isActiveLookupItem), [typesRaw]);
  const fileTypes = useMemo(() => fileTypesRaw.filter(isActiveLookupItem), [fileTypesRaw]);
  const users = useMemo(() => asList<User>(usersRaw).filter(isActiveLookupItem), [usersRaw]);
  const departments = useMemo(() => asList<Department>(departmentsRaw).filter(isActiveLookupItem), [departmentsRaw]);
  const ranks = useMemo(() => asList<CrewRank>(ranksRaw).filter(isActiveLookupItem), [ranksRaw]);
  const [editing, setEditing] = useState<
    (Omit<ApprovalTemplate, 'id' | 'steps'> & { id?: number; steps: StepDraft[] }) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterTypeId, setFilterTypeId] = useState<number | 'all' | 'uncategorized'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [typeSidebarFilter, setTypeSidebarFilter] = useState('');

  const typeNavItems = useMemo(() => {
    const uncategorizedCount = templates.filter((t) => !t.approvalTypeId && !t.approvalType).length;
    return [
      { id: 'all' as const, label: 'All types', code: null as string | null, count: templates.length },
      ...types.map((type) => ({
        id: type.id as number,
        label: type.name,
        code: type.code,
        count: templates.filter((t) => t.approvalTypeId === type.id).length,
      })),
      ...(uncategorizedCount > 0
        ? [
            {
              id: 'uncategorized' as const,
              label: 'Uncategorized',
              code: null as string | null,
              count: uncategorizedCount,
            },
          ]
        : []),
    ];
  }, [templates, types]);

  const filteredTypeNavItems = useMemo(() => {
    const q = typeSidebarFilter.trim().toLowerCase();
    if (!q) return typeNavItems;
    return typeNavItems.filter(
      (item) =>
        item.id === 'all' ||
        item.label.toLowerCase().includes(q) ||
        (item.code || '').toLowerCase().includes(q),
    );
  }, [typeNavItems, typeSidebarFilter]);

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (filterTypeId === 'uncategorized') {
      list = list.filter((t) => !t.approvalTypeId && !t.approvalType);
    } else if (filterTypeId !== 'all') {
      list = list.filter((t) => t.approvalTypeId === filterTypeId);
    }
    const q = searchInput.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.code.toLowerCase().includes(q) ||
          (t.contextKey || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [templates, filterTypeId, searchInput]);

  const selectedTypeLabel =
    filterTypeId === 'all'
      ? 'All types'
      : filterTypeId === 'uncategorized'
        ? 'Uncategorized'
        : types.find((t) => t.id === filterTypeId)?.name || 'Type';

  const openEditor = (template?: ApprovalTemplate) => {
    setError(null);
    setEditing(
      template
        ? {
            ...template,
            steps: (() => {
              const maxStage = Math.max(...template.steps.map((s) => s.stage), 1);
              return template.steps.map((s, index) => {
                const label = s.label || 'Review';
                const defaults = defaultsForType(label, s.stage >= maxStage);
                return {
                  stage: s.stage,
                  sequence: s.sequence ?? index,
                  label,
                  stageMode: s.stageMode === 'ANY' ? 'ANY' : 'ALL',
                  onApprove: s.onApprove ?? defaults.onApprove,
                  onReject: s.onReject ?? defaults.onReject,
                  assigneeUserId: s.assigneeUserId ?? undefined,
                  assigneeDepartmentId: s.assigneeDepartmentId ?? undefined,
                  assigneeRankId: s.assigneeRankId ?? undefined,
                  assignMode: s.assigneeRankId
                    ? ('rank' as const)
                    : s.assigneeDepartmentId
                      ? ('department' as const)
                      : ('user' as const),
                  slaHours: s.slaHours ?? undefined,
                  escalateUserId: s.escalateUserId ?? undefined,
                  escalateDepartmentId: s.escalateDepartmentId ?? undefined,
                };
              });
            })(),
          }
        : empty(),
    );
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        code: editing.code.trim(),
        name: editing.name.trim(),
        approvalTypeId: editing.approvalTypeId,
        isActive: editing.isActive ?? true,
        departmentId: editing.departmentId || undefined,
        contextKey: editing.contextKey || undefined,
        minAmount: editing.minAmount != null && editing.minAmount !== '' ? Number(editing.minAmount) : undefined,
        maxAmount: editing.maxAmount != null && editing.maxAmount !== '' ? Number(editing.maxAmount) : undefined,
        steps: editing.steps.map((step, index) => ({
          stage: Number(step.stage),
          sequence: Number(step.sequence ?? index),
          label: step.label || undefined,
          stageMode: step.stageMode,
          onApprove: step.onApprove,
          onReject: step.onReject,
          assigneeUserId: step.assigneeUserId || undefined,
          assigneeDepartmentId: step.assigneeDepartmentId || undefined,
          assigneeRankId: step.assigneeRankId || undefined,
          slaHours: step.slaHours || undefined,
          escalateUserId: step.escalateUserId || undefined,
          escalateDepartmentId: step.escalateDepartmentId || undefined,
        })),
      };
      if (editing.id) await updateApprovalTemplate(editing.id, body);
      else await createApprovalTemplate(body);
      setEditing(null);
      await mutate();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to save template';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  const setStep = (index: number, update: Partial<StepDraft>) =>
    setEditing((current) =>
      current
        ? { ...current, steps: current.steps.map((step, i) => (i === index ? { ...step, ...update } : step)) }
        : current,
    );

  if (editing) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto flex max-w-6xl items-start gap-6">
          <form onSubmit={save} className={cn('min-w-0 flex-1 overflow-hidden rounded-2xl border shadow-sm lg:max-w-3xl', colors.surface, colors.border)}>
          <div className={cn('flex items-start justify-between gap-3 border-b px-5 py-4 md:px-6', colors.border)}>
            <div className="flex min-w-0 items-start gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditing(null);
                  setError(null);
                }}
                aria-label="Back"
                title="Back"
                className="mt-0.5 shrink-0"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
              <h2 className={cn('text-lg font-semibold', colors.text)}>
                {editing.id ? 'Edit template' : 'New template'}
              </h2>
              <p className={cn('mt-0.5 text-sm', colors.textMuted)}>
                Define who reviews or approves, and in what order.
              </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
              aria-label="Close"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-6 px-5 py-5 md:px-6">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </div>
            )}

            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>Basics</h3>
                <Switch
                  label={editing.isActive === false ? 'Inactive' : 'Active'}
                  checked={editing.isActive !== false}
                  onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                  size="sm"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label required>Name</Label>
                  <TextInput
                    required
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Default approval flow"
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label required>Code</Label>
                  <TextInput
                    required
                    value={editing.code}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                    placeholder="DEFAULT_FLOW"
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label required>Approval type</Label>
                  <Select
                    required
                    value={editing.approvalTypeId || ''}
                    placeholder="Select type"
                    showClearButton={false}
                    options={types.map((type) => ({ value: type.id, label: type.name }))}
                    onChange={(e) =>
                      setEditing({ ...editing, approvalTypeId: e.target.value ? Number(e.target.value) : 0 })
                    }
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>Matching rules</h3>
              <p className={cn('text-sm', colors.textMuted)}>
                Optional filters — leave as “Any” for the default template of this type.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Context key</Label>
                  <TextInput
                    list="approval-context-keys"
                    value={editing.contextKey || ''}
                    onChange={(e) => setEditing({ ...editing, contextKey: e.target.value || null })}
                    placeholder="Any context (optional)"
                    showClearButton
                    helperText="Optional match key for the source module (e.g. document file type INVOICE). Leave empty for the default template of this approval type."
                  />
                  <datalist id="approval-context-keys">
                    {fileTypes.map((type) => (
                      <option value={type.code} key={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <Label>Department</Label>
                  <Select
                    value={editing.departmentId || ''}
                    placeholder="Any department"
                    showClearButton
                    options={departments.map((d) => ({
                      value: d.id,
                      label: d.name || d.departmentName || `Department #${d.id}`,
                    }))}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        departmentId: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Min amount</Label>
                  <NumberInput
                    min={0}
                    step={0.01}
                    value={editing.minAmount ?? ''}
                    onChange={(e) => setEditing({ ...editing, minAmount: e.target.value })}
                    placeholder="No minimum"
                    showClearButton
                  />
                </div>
                <div>
                  <Label>Max amount</Label>
                  <NumberInput
                    min={0}
                    step={0.01}
                    value={editing.maxAmount ?? ''}
                    onChange={(e) => setEditing({ ...editing, maxAmount: e.target.value })}
                    placeholder="No maximum"
                    showClearButton
                    helperText="Inclusive band used for threshold routing (e.g. route larger invoices to a stricter template)."
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>Workflow</h3>
                  <p className={cn('mt-1 text-sm', colors.textMuted)}>
                    Stages run one after another. People in the same stage all must approve before the next stage starts.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    const prevMax = Math.max(0, ...editing.steps.map((s) => s.stage));
                    const nextStage = prevMax + 1;
                    setEditing({
                      ...editing,
                      steps: [
                        ...editing.steps.map((step) =>
                          step.stage === prevMax && step.onApprove === 'COMPLETE'
                            ? { ...step, onApprove: 'CONTINUE' as const }
                            : step,
                        ),
                        {
                          stage: nextStage,
                          sequence: 0,
                          label: 'Review',
                          assignMode: 'user',
                          stageMode: 'ALL',
                          ...defaultsForType('Review', true),
                        },
                      ],
                    });
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add next stage
                </Button>
              </div>

              <div
                className={cn(
                  'flex gap-3 rounded-xl border px-3.5 py-3 text-sm',
                  colors.border,
                  'bg-slate-50/80 dark:bg-slate-800/40',
                )}
              >
                <InfoIcon className={cn('mt-0.5 h-4 w-4 shrink-0', colors.textMuted)} />
                <div className={cn('space-y-1', colors.textMuted)}>
                  <p>
                    <span className={cn('font-medium', colors.text)}>Add next stage</span>
                    {' — '}must wait for the previous stage to finish first.
                  </p>
                  <p>
                    <span className={cn('font-medium', colors.text)}>Add parallel approver</span>
                    {' — '}same stage; choose whether all must approve or any one can.
                  </p>
                </div>
              </div>

              <div className="space-y-0">
                {groupStepsByStage(editing.steps).map((group, groupIndex, groups) => {
                  const isParallel = group.indices.length > 1;
                  const stageMode = editing.steps[group.indices[0]]?.stageMode === 'ANY' ? 'ANY' : 'ALL';
                  const setStageMode = (mode: ApprovalStageMode) =>
                    setEditing({
                      ...editing,
                      steps: editing.steps.map((step, i) =>
                        group.indices.includes(i) ? { ...step, stageMode: mode } : step,
                      ),
                    });
                  return (
                    <div key={group.stage}>
                      {groupIndex > 0 && (
                        <div className="flex flex-col items-center py-2">
                          <div className={cn('h-4 w-px', 'bg-slate-300 dark:bg-slate-600')} />
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                              'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                            )}
                          >
                            <ChevronDownIcon className="h-3 w-3" />
                            Then
                          </span>
                          <div className={cn('h-4 w-px', 'bg-slate-300 dark:bg-slate-600')} />
                        </div>
                      )}

                      <div className={cn('overflow-hidden rounded-2xl border', colors.border)}>
                        <div
                          className={cn(
                            'flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3',
                            colors.border,
                            'bg-slate-50/90 dark:bg-slate-800/50',
                          )}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-indigo-600 px-2 text-xs font-bold text-white">
                              {groupIndex + 1}
                            </span>
                            <span className={cn('text-sm font-semibold', colors.text)}>
                              Stage {groupIndex + 1}
                            </span>
                            {isParallel ? (
                              <div className="flex gap-1">
                                {(['ALL', 'ANY'] as const).map((mode) => (
                                  <Button
                                    key={mode}
                                    type="button"
                                    size="sm"
                                    variant={stageMode === mode ? 'default' : 'outline'}
                                    onClick={() => setStageMode(mode)}
                                    className="rounded-full px-2.5 py-0.5 text-[11px] h-auto"
                                  >
                                    {mode === 'ALL' ? 'All must approve' : 'Any one can approve'}
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <Badge variant="secondary">Single approver</Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-indigo-600 dark:text-indigo-400"
                            onClick={() => {
                              const isLast =
                                group.stage >= Math.max(...editing.steps.map((s) => s.stage));
                              setEditing({
                                ...editing,
                                steps: [
                                  ...editing.steps,
                                  {
                                    stage: group.stage,
                                    sequence: group.indices.length,
                                    label: 'Review',
                                    assignMode: 'user',
                                    stageMode,
                                    ...defaultsForType('Review', isLast),
                                  },
                                ],
                              });
                            }}
                          >
                            <UserPlusIcon className="h-3.5 w-3.5" />
                            Add parallel approver
                          </Button>
                        </div>

                        <div className={cn('divide-y', 'divide-slate-200 dark:divide-slate-700')}>
                          {group.indices.map((index, stepInStage) => {
                            const step = editing.steps[index];
                            const assignMode = step.assignMode ?? (step.assigneeDepartmentId ? 'department' : 'user');
                            return (
                              <div key={index} className="p-4">
                                <div className="mb-3 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    {isParallel && (
                                      <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                                        Approver {stepInStage + 1}
                                      </span>
                                    )}
                                    <span className={cn('text-sm font-semibold', colors.text)}>
                                      {step.label || `Step ${index + 1}`}
                                    </span>
                                  </div>
                                  {editing.steps.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        setEditing({
                                          ...editing,
                                          steps: editing.steps.filter((_, i) => i !== index),
                                        })
                                      }
                                      className="h-8 w-8 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                                      aria-label="Remove step"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <div>
                                    <Label required>Type</Label>
                                    <Select
                                      required
                                      value={
                                        STEP_TYPE_OPTIONS.includes(step.label as (typeof STEP_TYPE_OPTIONS)[number])
                                          ? step.label!
                                          : ''
                                      }
                                      placeholder="Select type…"
                                      showClearButton={false}
                                      options={STEP_TYPE_OPTIONS.map((option) => ({
                                        value: option,
                                        label: option,
                                      }))}
                                      onChange={(e) => {
                                        const label = e.target.value;
                                        const isLast =
                                          step.stage >= Math.max(...editing.steps.map((s) => s.stage));
                                        setStep(index, { label, ...defaultsForType(label, isLast) });
                                      }}
                                    />
                                  </div>

                                  <div>
                                    <Label>Assign to</Label>
                                    <div className="mb-2 flex flex-wrap gap-2">
                                      {(['user', 'department', 'rank'] as const).map((mode) => (
                                        <Button
                                          key={mode}
                                          type="button"
                                          size="sm"
                                          variant={assignMode === mode ? 'default' : 'outline'}
                                          className="capitalize"
                                          onClick={() =>
                                            setStep(index, {
                                              assignMode: mode,
                                              assigneeUserId: mode === 'user' ? step.assigneeUserId : undefined,
                                              assigneeDepartmentId:
                                                mode === 'department' ? step.assigneeDepartmentId : undefined,
                                              assigneeRankId: mode === 'rank' ? step.assigneeRankId : undefined,
                                            })
                                          }
                                        >
                                          {mode}
                                        </Button>
                                      ))}
                                    </div>
                                    {assignMode === 'user' ? (
                                      <Select
                                        required
                                        value={step.assigneeUserId || ''}
                                        placeholder="Select user"
                                        showClearButton={false}
                                        options={users.map((user) => ({
                                          value: user.id,
                                          label: userLabel(user),
                                        }))}
                                        onChange={(e) =>
                                          setStep(index, {
                                            assignMode: 'user',
                                            assigneeUserId: e.target.value ? Number(e.target.value) : undefined,
                                            assigneeDepartmentId: undefined,
                                            assigneeRankId: undefined,
                                          })
                                        }
                                      />
                                    ) : assignMode === 'department' ? (
                                      <Select
                                        required
                                        value={step.assigneeDepartmentId || ''}
                                        placeholder="Select department"
                                        showClearButton={false}
                                        options={departments.map((d) => ({
                                          value: d.id,
                                          label: d.name || d.departmentName || `Department #${d.id}`,
                                        }))}
                                        onChange={(e) =>
                                          setStep(index, {
                                            assignMode: 'department',
                                            assigneeDepartmentId: e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                            assigneeUserId: undefined,
                                            assigneeRankId: undefined,
                                          })
                                        }
                                      />
                                    ) : (
                                      <>
                                        <Select
                                          required
                                          value={step.assigneeRankId || ''}
                                          placeholder="Select rank"
                                          showClearButton={false}
                                          options={ranks.map((rank) => ({
                                            value: rank.id,
                                            label: rankLabel(rank),
                                          }))}
                                          onChange={(e) =>
                                            setStep(index, {
                                              assignMode: 'rank',
                                              assigneeRankId: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                              assigneeUserId: undefined,
                                              assigneeDepartmentId: undefined,
                                            })
                                          }
                                        />
                                        <p className={cn('mt-1 text-xs', colors.textMuted)}>
                                          Routes to whoever holds this rank aboard the request's vessel.
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                  <div>
                                    <Label>SLA (hours)</Label>
                                    <NumberInput
                                      min={1}
                                      step={1}
                                      allowDecimals={false}
                                      value={step.slaHours ?? ''}
                                      onChange={(e) =>
                                        setStep(index, {
                                          slaHours: e.target.value == null ? undefined : Number(e.target.value),
                                        })
                                      }
                                      placeholder="No SLA"
                                      showClearButton
                                    />
                                  </div>
                                  <div>
                                    <Label>Escalate to (if overdue)</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                      <Select
                                        value={step.escalateUserId || ''}
                                        placeholder="No escalation user"
                                        showClearButton
                                        options={users.map((user) => ({
                                          value: user.id,
                                          label: userLabel(user),
                                        }))}
                                        onChange={(e) =>
                                          setStep(index, {
                                            escalateUserId: e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                            escalateDepartmentId: e.target.value
                                              ? undefined
                                              : step.escalateDepartmentId,
                                          })
                                        }
                                      />
                                      <Select
                                        value={step.escalateDepartmentId || ''}
                                        placeholder="No escalation dept"
                                        showClearButton
                                        options={departments.map((d) => ({
                                          value: d.id,
                                          label: d.name || d.departmentName || `Department #${d.id}`,
                                        }))}
                                        onChange={(e) =>
                                          setStep(index, {
                                            escalateDepartmentId: e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                            escalateUserId: e.target.value
                                              ? undefined
                                              : step.escalateUserId,
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                  <div>
                                    <Label>On approve</Label>
                                    <Select
                                      value={step.onApprove}
                                      showClearButton={false}
                                      options={[
                                        { value: 'CONTINUE', label: 'Continue to next stage' },
                                        { value: 'COMPLETE', label: 'Complete — mark Approved' },
                                      ]}
                                      onChange={(e) =>
                                        setStep(index, {
                                          onApprove: e.target.value as ApprovalOnApproveAction,
                                        })
                                      }
                                    />
                                  </div>
                                  <div>
                                    <Label>On reject</Label>
                                    <Select
                                      value={step.onReject}
                                      showClearButton={false}
                                      options={[
                                        { value: 'REJECT', label: 'Final rejection' },
                                        { value: 'RETURN', label: 'Return to submitter (Draft)' },
                                      ]}
                                      onChange={(e) =>
                                        setStep(index, {
                                          onReject: e.target.value as ApprovalOnRejectAction,
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {groupIndex === groups.length - 1 && groups.length === 1 && (
                        <p className={cn('mt-3 text-center text-xs', colors.textMuted)}>
                          Need someone else after this? Use “Add next stage”.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <div className={cn('flex justify-end gap-2 border-t px-5 py-4 md:px-6', colors.border)}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} loading={saving}>
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
          </form>

          <aside className="hidden w-72 shrink-0 lg:sticky lg:top-6 lg:block xl:w-80">
            <WorkflowFlowchart
              name={editing.name}
              steps={editing.steps}
              users={users}
              departments={departments}
              ranks={ranks}
              colors={colors}
            />
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <div
        className={cn(
          'flex min-h-0 flex-1 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <aside className={cn('flex w-64 shrink-0 flex-col min-h-0 border-r sm:w-72', colors.border, colors.surfaceHover)}>
          <div className={cn('space-y-2.5 border-b px-3 pb-2 pt-3', colors.border, colors.surface)}>
            <div className="flex items-center justify-between gap-2">
              <h2 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
                Approval types
              </h2>
              <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>{types.length}</span>
            </div>
            <TextInput
              value={typeSidebarFilter}
              onChange={(e) => setTypeSidebarFilter(e.target.value)}
              placeholder="Filter types…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(typeSidebarFilter)}
              size="sm"
            />
          </div>

          <div className={cn('dashboard-scrollbar flex-1 overflow-y-auto p-1.5', colors.surface)}>
            <div className="flex flex-col gap-0.5" role="listbox" aria-label="Filter by approval type">
              {filteredTypeNavItems.map((item) => {
                const active = filterTypeId === item.id;
                return (
                  <button
                    key={String(item.id)}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => setFilterTypeId(item.id)}
                    title={item.label}
                    className={cn(
                      'min-w-0 rounded-md px-2.5 py-2 text-left transition-colors',
                      active
                        ? cn(colors.info, 'ring-1', colors.primaryBorderAll)
                        : cn(colors.text, colors.interactiveHover),
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="block truncate text-sm font-medium">{item.label}</span>
                      </div>
                      <span
                        className={cn(
                          'mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                          active
                            ? cn(colors.primary, colors.primaryText)
                            : cn(colors.surfaceHover, colors.textMuted),
                        )}
                      >
                        {item.count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            {filteredTypeNavItems.length === 0 && (
              <p className={cn('px-3 py-8 text-center text-sm', colors.textMuted)}>No types match.</p>
            )}
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            className={cn(
              'flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-2.5',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Badge variant="info" className="font-normal">
                {selectedTypeLabel}
              </Badge>
              <span className={cn('text-sm', colors.textMuted)}>
                {isLoading
                  ? 'Loading…'
                  : `${filteredTemplates.length} template${filteredTemplates.length === 1 ? '' : 's'}`}
              </span>
            </div>
            <Button type="button" onClick={() => openEditor()} className="gap-1.5 shrink-0">
              <PlusIcon className="h-4 w-4" />
              New template
            </Button>
          </div>

          <div className={cn('flex-shrink-0 border-b px-4 py-2', colors.border)}>
            <TextInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search templates…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(searchInput)}
              size="sm"
            />
          </div>

          {isLoading ? (
            <div className={cn('flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20', colors.textMuted)}>
              <SpinnerIcon className="h-6 w-6" />
              <p className="text-sm">Loading templates…</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <ListEmptyState
              icon={<DocumentTextIcon className="h-12 w-12" />}
              title={templates.length === 0 ? 'No templates yet' : 'No matching templates'}
              hint={
                templates.length === 0
                  ? 'Create a template with stages and assignees so approvals can be submitted.'
                  : 'Try another type or clear the search.'
              }
              action={
                <Button type="button" onClick={() => openEditor()} className="gap-1.5">
                  <PlusIcon className="h-4 w-4" />
                  New template
                </Button>
              }
            />
          ) : (
            <div className="min-h-0 flex-1 overflow-auto [&_table_th]:!px-3 [&_table_th]:!py-3 [&_table_td]:!px-3 [&_table_td]:!py-3.5">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                    <th className="w-[5.5rem] font-medium" />
                    <th className="font-medium">Name</th>
                    <th className="hidden w-40 font-medium sm:table-cell">Type</th>
                    <th className="hidden w-44 font-medium lg:table-cell">Matching</th>
                    <th className="hidden w-48 font-medium md:table-cell">Stages</th>
                    <th className="w-28 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map((template) => {
                    const stageGroups = groupStepsByStage(template.steps);
                    const matchingParts = [
                      template.contextKey || 'Any context',
                      template.department
                        ? template.department.name || template.department.departmentName
                        : 'Any department',
                      (template.minAmount != null || template.maxAmount != null) &&
                        formatAmountBand(template.minAmount, template.maxAmount),
                    ].filter(Boolean);
                    return (
                      <tr
                        key={template.id}
                        onClick={() => openEditor(template)}
                        className={cn(
                          'group cursor-pointer border-b transition last:border-b-0',
                          colors.border,
                          colors.interactiveHover,
                        )}
                      >
                        <td>
                          <div className="flex flex-nowrap items-center">
                            <button
                              type="button"
                              title="Edit"
                              aria-label="Edit template"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditor(template);
                              }}
                              className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              aria-label="Delete template"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await confirm({
                                  title: 'Delete template',
                                  message: `Delete template “${template.name}”?`,
                                  confirmText: 'Delete',
                                  cancelText: 'Cancel',
                                  confirmVariant: 'danger',
                                });
                                if (!ok) return;
                                await deleteApprovalTemplate(template.id);
                                await mutate();
                              }}
                              className={cn(
                                'rounded-lg p-1 transition-colors',
                                isDark ? 'text-red-400' : 'text-red-600',
                                colors.interactiveHover,
                              )}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                        <td className="max-w-0">
                          <div className="min-w-0">
                            <p className={cn('truncate font-medium', colors.text)}>{template.name}</p>
                            <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>{template.code}</p>
                            <p className={cn('mt-1 truncate text-xs sm:hidden', colors.textMuted)}>
                              {template.approvalType?.name || 'Uncategorized'}
                            </p>
                          </div>
                        </td>
                        <td className={cn('hidden truncate sm:table-cell', colors.text)}>
                          {template.approvalType?.name || 'Uncategorized'}
                        </td>
                        <td className={cn('hidden truncate lg:table-cell', colors.textMuted)}>
                          {matchingParts.join(' · ')}
                        </td>
                        <td className="hidden md:table-cell">
                          {stageGroups.length === 0 ? (
                            <span className={colors.textMuted}>—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {stageGroups.map((group, groupIndex) => {
                                const stepsInStage = group.indices.map((i) => template.steps[i]);
                                const labels = stepsInStage.map((s) => s.label || 'Step').join(' · ');
                                const isParallel = group.indices.length > 1;
                                const stageMode = stepsInStage[0]?.stageMode === 'ANY' ? 'ANY' : 'ALL';
                                return (
                                  <span
                                    key={group.stage}
                                    className={cn(
                                      'inline-flex max-w-full items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[11px] font-medium',
                                      colors.border,
                                      isParallel
                                        ? stageMode === 'ANY'
                                          ? isDark
                                            ? 'bg-amber-400/15 text-amber-200'
                                            : 'bg-amber-50/80 text-amber-800'
                                          : cn(colors.info)
                                        : isDark
                                          ? 'bg-white/[0.06] text-slate-200'
                                          : 'bg-slate-50 text-slate-700',
                                    )}
                                    title={labels}
                                  >
                                    <span
                                      className={cn(
                                        'flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[8px] font-bold',
                                        colors.primary,
                                        colors.primaryText,
                                      )}
                                    >
                                      {groupIndex + 1}
                                    </span>
                                    <span className="truncate">{labels}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          <Badge variant={template.isActive === false ? 'secondary' : 'success'}>
                            {template.isActive === false ? 'Inactive' : 'Active'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
